import React from 'react';
import './AccountPageHelp.scss';
import { ACCOUNT_TYPE_HINTS, STATUS_HINTS } from './accountHints';
import { MODEL_CATALOG } from './modelCatalog';
import { addonDescriptions, allAddons } from '../../../../shared/types/account/Addons';
import { allMonitorTypes } from '../../../../shared/types/monitor/MonitorType';

/**
 * The account page's Help tab. The hand-written part is the list of TOPICS below — add one when a block or
 * field lands on the page. The reference lists (statuses, plans, models, addons) are built from the same
 * constants the form uses, so they follow every change on their own.
 */
const TOPICS: { title: string; text: string }[] = [
  {
    title: 'Saving',
    text:
      'Every block saves on its own with its Save button; the orange frame shows which block has unsaved ' +
      'edits and the row under it lists the changed fields. Red frame = something in that block is invalid ' +
      '(or the server refused it) — fix it, Save comes back. Cancel puts that block back to what is saved. ' +
      'Nothing else on the page is touched by a save.',
  },
  {
    title: 'Account status vs. type',
    text:
      'Status is where the account is in the billing cycle (crons and payment webhooks keep rewriting it). ' +
      'Type is the plan bought in Paddle — read-only here. Ban is separate from both: a banned account ' +
      'shows only a suspension screen, every project and prompt is paused, and nothing (payment, cron) ' +
      'un-pauses it until Unban.',
  },
  {
    title: 'Limits & schedule',
    text:
      'Question limit = prompts the account may run; User limit = seats. Trial expiration moves the end of ' +
      'the trial (the number is its length in days). The monthly report quotas: empty = plan default, 0 = ' +
      'blocked, any number = override. Monitoring frequency is the default for new projects; without the ' +
      'MANAGE_MONITORING_SCHEDULE addon saving it re-pins EVERY existing project, with the addon they keep ' +
      'their own — "Sync projects" re-pins them by hand.',
  },
  {
    title: 'AI models',
    text:
      'Which models the account’s prompts are queried on, every run. Ticking a model starts collecting for ' +
      'all of its projects from the next run; unticking stops it (history stays).',
  },
  {
    title: 'Addons',
    text:
      'Feature switches — pages, exports, integrations. Some imply others (shown ticked and locked). ' +
      'BILLING / TEST_BILLING cannot be changed from here.',
  },
  {
    title: 'Plan templates',
    text:
      'A named models + addons set. "Apply & save" replaces BOTH lists on the account in one save. Templates ' +
      'are created and edited under "Manage templates" (or "Plan templates" on the accounts list) and need ' +
      'the Plan templates permission.',
  },
  {
    title: 'Right column',
    text:
      'Subscription & limits and Costs are read-only mirrors (Costs needs its own permission and is estimated ' +
      'from metered usage). Recent activity shows the newest entries of both logs — the full admin log and ' +
      'the client’s own activity are under History.',
  },
  {
    title: 'Login as user',
    text:
      'Opens the app with the client’s own token. Everything you do there is theirs — the admin log marks it ' +
      'as a superadmin action. "Return to admin" in the banner brings you back.',
  },
];

const AccountPageHelp: React.FC = () => (
  <div className="account-help">
    {TOPICS.map((topic) => (
      <section key={topic.title} className="account-help__topic">
        <h4 className="account-help__title">{topic.title}</h4>
        <p className="account-help__text">{topic.text}</p>
      </section>
    ))}

    <section className="account-help__topic">
      <h4 className="account-help__title">Statuses</h4>
      <dl className="account-help__list">
        {(Object.keys(STATUS_HINTS) as (keyof typeof STATUS_HINTS)[]).map((status) => (
          <React.Fragment key={status}>
            <dt>{status}</dt>
            <dd>{STATUS_HINTS[status]}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>

    <section className="account-help__topic">
      <h4 className="account-help__title">Plans</h4>
      <dl className="account-help__list">
        {(Object.keys(ACCOUNT_TYPE_HINTS) as (keyof typeof ACCOUNT_TYPE_HINTS)[]).map((type) => (
          <React.Fragment key={type}>
            <dt>{type}</dt>
            <dd>{ACCOUNT_TYPE_HINTS[type]}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>

    <section className="account-help__topic">
      <h4 className="account-help__title">Models</h4>
      <dl className="account-help__list">
        {allMonitorTypes.map((model) => (
          <React.Fragment key={model}>
            <dt>{model}</dt>
            <dd>
              {MODEL_CATALOG[model]
                ? `${MODEL_CATALOG[model].provider} — ${MODEL_CATALOG[model].description}`
                : '—'}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </section>

    <section className="account-help__topic">
      <h4 className="account-help__title">Addons</h4>
      <dl className="account-help__list">
        {allAddons.map((addon) => (
          <React.Fragment key={addon}>
            <dt>{addon}</dt>
            <dd>{addonDescriptions[addon]}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  </div>
);

export default AccountPageHelp;
